import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUsers, 
  faSpinner, 
  faArchive,
  faUndo,
  faUser,
  faCalendarAlt,
  faUserTag,
  faExclamationTriangle,
  faFilter
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../Api';
import ConfirmationModal from '../../components/ConfirmationModal';
import SuccessModal from '../../components/SuccessModal';

const ArchiveUsers = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [archivedUsers, setArchivedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [restoring, setRestoring] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [userToRestore, setUserToRestore] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');


  const [roleFilter, setRoleFilter] = useState('all');

  const fetchArchivedUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/archive-users');
      if (response.data.success) {
        let filteredUsers = response.data.data;
        
        // Filter based on user role
        if (currentUser?.role === 'charge_etude_superieur') {
          // Only show archived chargés d'études
          filteredUsers = response.data.data.filter(user => user.role === 'charge_etude');
        } else if (currentUser?.role === 'chef_terrain_superieur') {
          // Show archived chefs de terrain, resp_controle, and resp_cati
          filteredUsers = response.data.data.filter(user => 
            user.role === 'chef_terrain' || user.role === 'resp_controle' || user.role === 'resp_cati'
          );
          // Apply role filter if set
          if (roleFilter !== 'all') {
            filteredUsers = filteredUsers.filter(user => user.role === roleFilter);
          }
        }
        // For administrateur_general, show all archived users
        
        setArchivedUsers(filteredUsers);
      } else {
        setError('Erreur lors du chargement des responsables archivés');
      }
    } catch (error) {
      console.error('Error fetching archived users:', error);
      setError('Erreur lors du chargement des responsables archivés');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArchivedUsers();
  }, [roleFilter]);

  const handleRestoreClick = (user) => {
    setUserToRestore(user);
    setShowConfirmModal(true);
  };

  const handleRestore = async () => {
    if (!userToRestore) return;
    
    try {
      setRestoring(userToRestore.id);
      setShowConfirmModal(false);
      
      await api.post(`/archive-users/${userToRestore.id}/restore`);
      fetchArchivedUsers(); // Refresh the list
      
      setSuccessMessage('Chargé d\'études restauré avec succès!');
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error restoring user:', error);
      setSuccessMessage(error.response?.data?.message || 'Erreur lors de la restauration du chargé d\'études');
      setShowSuccessModal(true);
    } finally {
      setRestoring(null);
      setUserToRestore(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Jamais';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 relative overflow-hidden flex items-center justify-center">
        {/* Dotted Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }}></div>
        </div>
        <div className="text-center relative z-10">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">
            {currentUser?.role === 'charge_etude_superieur' ? 'Chargement des charges d\'études archivées...' : 
             currentUser?.role === 'chef_terrain_superieur' ? 'Chargement des chefs de terrain archivés...' : 
             'Chargement des responsables archivés...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Dotted Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
      </div>
      
      {/* Header */}
      <header className="relative z-10 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 p-6 animate-fade-in">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 text-center sm:text-left">
            {currentUser?.role === 'charge_etude_superieur' ? 'CHARGES D\'ÉTUDES ARCHIVÉES' : 
             currentUser?.role === 'chef_terrain_superieur' ? 'RESPONSABLES ARCHIVÉS' : 
             'RESPONSABLES ARCHIVÉS'}
          </h1>
        </div>
        <button 
          onClick={() => navigate(
            currentUser?.role === 'charge_etude_superieur' ? '/charge-etudes' : 
            currentUser?.role === 'chef_terrain_superieur' ? '/responsables' : 
            '/users'
          )}
          className="w-full sm:w-auto px-6 py-3 bg-red-600 text-white rounded-xl transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:scale-95 active:shadow-md active:translate-y-0"
        >
          <FontAwesomeIcon icon={faUsers} className="mr-2" />
          {currentUser?.role === 'charge_etude_superieur' ? 'Charges d\'Études' : 
           currentUser?.role === 'chef_terrain_superieur' ? 'Responsables' : 
           'Responsables'}
        </button>
      </header>

      {/* Main content */}
      <div className="relative z-10 p-6 animate-slide-up">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600 mr-2" />
              <p className="text-red-600">{error}</p>
            </div>
          </div>
        )}

        {/* Role Filter for chef_terrain_superieur */}
        {currentUser?.role === 'chef_terrain_superieur' && (
          <div className="mb-6 bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center space-x-2">
                <FontAwesomeIcon icon={faFilter} className="text-gray-500" />
                <span className="font-medium text-gray-700">Filtrer par rôle:</span>
              </div>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none"
              >
                <option value="all">Tous les rôles</option>
                <option value="chef_terrain">Chef de Terrain</option>
                <option value="resp_controle">Responsable Contrôle</option>
                <option value="resp_cati">Responsable CATI</option>
              </select>
            </div>
          </div>
        )}

        {/* Archived Users Table */}
        <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 overflow-hidden">
          {archivedUsers.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <FontAwesomeIcon icon={faArchive} className="text-4xl text-gray-300 mb-4" />
              <p className="text-lg">
                {currentUser?.role === 'charge_etude_superieur' ? 'Aucune charge d\'études archivée' : 
                 currentUser?.role === 'chef_terrain_superieur' ? 'Aucun responsable archivé' : 
                 'Aucun responsable archivé'}
              </p>
              <p className="text-sm">
                {currentUser?.role === 'charge_etude_superieur' ? 'Les charges d\'études archivées apparaîtront ici' : 
                 currentUser?.role === 'chef_terrain_superieur' ? 'Les responsables archivés apparaîtront ici' : 
                 'Les responsables archivés apparaîtront ici'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {currentUser?.role === 'charge_etude_superieur' ? 'Charge d\'Études' : 
                       'Responsable'}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rôle
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Archivé le
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Raison
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Archivé par
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {archivedUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3">
                            <FontAwesomeIcon icon={faUser} className="text-red-600" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{user.name}</div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                            <div className="text-xs text-gray-400">ID: {user.original_id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">{user.role_display}</span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${user.status_color}`}>
                          {user.status_text}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-500">
                          <FontAwesomeIcon icon={faCalendarAlt} className="mr-1" />
                          {formatDate(user.archived_at)}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate" title={user.archive_reason}>
                          {user.archive_reason}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-500">
                          <FontAwesomeIcon icon={faUserTag} className="mr-1" />
                          {user.archived_by}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleRestoreClick(user)}
                          disabled={restoring === user.id}
                          className="text-green-600 hover:text-green-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                        >
                          {restoring === user.id ? (
                            <>
                              <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-1" />
                              Restauration...
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faUndo} className="mr-1" />
                              Restaurer
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleRestore}
        title="Confirmer la restauration"
        message={`Êtes-vous sûr de vouloir restaurer ${userToRestore?.name || 'ce chargé d\'études'} ?`}
        confirmText="Restaurer"
        cancelText="Annuler"
        isLoading={restoring !== null}
      />

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Opération terminée"
        message={successMessage}
      />
    </div>
  );
};

export default ArchiveUsers;
